import type { GetFilenames } from "@/models/api/getFilenames";
import type { DateHour } from "@/models/common";
import type { GetFilenamesResponse } from "@/api/getFilenames";
import type { Services } from "@/models/common";

/**
 * Takes an array of DateHours and forms a GetFilename payload for each ordered pair
 * @param allDateHours Array of DateHours from the start and end times
 * @param service Chooses between TRACES and LOGS service to build the payload param
 * @returns Array of GetFilenames payloads ready to be queried.
 */
export function getFilenamesPayloadsFromDateHours(
  allDateHours: DateHour[],
  service: Services
): GetFilenames[] {
  // Validation just in case something goes wrong
  if (allDateHours.length == 0)
    throw Error("Parsed dateHours arrays can not be empty!");

  const filenamePairParams = [] as GetFilenames[];
  if (allDateHours.length === 1)
    filenamePairParams.push({
      startDateHour: allDateHours[0].dateHour,
      startMinute: allDateHours[0].firstMinute,
      endDateHour: allDateHours[0].dateHour,
      endMinute: allDateHours[0].lastMinute,
      service: service,
    } as GetFilenames);
  else if (allDateHours.length > 1) {
    for (let i = 0; i < allDateHours.length - 1; i++) {
      filenamePairParams.push({
        startDateHour: allDateHours[i].dateHour,
        startMinute: allDateHours[i].firstMinute,
        endDateHour: allDateHours[i + 1].dateHour,
        endMinute: allDateHours[i + 1].lastMinute,
        service: service,
      } as GetFilenames);
    }
  }
  return filenamePairParams;
}

/**
 * Reduces an array of GetFilenamesResponses into a single merged response to then be used
 * as a single element
 * @param getFilenameResponses Array of GetFilenamesResponses
 * @returns Single GetFilenamesResponse with all the filenames merged together
 */
export function reduceGetFilenamesResponses(
  getFilenameResponses: GetFilenamesResponse[]
): GetFilenamesResponse {
  // Merge all results into a single object
  return getFilenameResponses.reduce((curr, next) => {
    return {
      results: {
        files: Array.from(
          new Set([
            ...(curr.results?.files as string[]),
            ...(next.results?.files as string[]),
          ])
        ),
        hasReportedData:
          curr.results?.hasReportedData || next.results?.hasReportedData,
      },
      success: curr.success || next.success,
    } as GetFilenamesResponse;
  });
}
